import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ShieldCheck, ShieldAlert, ShieldX, Trash2 } from "lucide-react";

import { getRecentChecks, clearRecentChecks } from "../lib/recentChecks";

const VERDICT_STYLE = {
  SAFE_TOGETHER: { label: "No label conflict found", color: "#0e6b34", bg: "#eefbf2", Icon: ShieldCheck },
  USE_WITH_CAUTION: { label: "Use with caution", color: "#b45309", bg: "#fdf3df", Icon: ShieldAlert },
  AVOID: { label: "Avoid", color: "#b91c1c", bg: "#fdeeea", Icon: ShieldX },
  INSUFFICIENT_DATA: { label: "Not enough verified data", color: "#7a7468", bg: "#f5ecde", Icon: ShieldAlert },
  // Legacy verdict codes from older localStorage entries.
  SAFE: { label: "No label conflict found", color: "#0e6b34", bg: "#eefbf2", Icon: ShieldCheck },
  MOSTLY_SAFE: { label: "No label conflict found", color: "#0e6b34", bg: "#eefbf2", Icon: ShieldCheck },
  CAUTION: { label: "Use with caution", color: "#b45309", bg: "#fdf3df", Icon: ShieldAlert },
};

/**
 * RecentChecksSection — homepage section that surfaces the last 5 anonymous
 * checks from localStorage with one-tap re-run deep links. Hidden entirely
 * when the list is empty so the homepage stays clean for first-time
 * visitors. Drives engagement by removing the friction of re-typing the
 * same medication + product.
 */
export default function RecentChecksSection() {
  const navigate = useNavigate();
  const [items, setItems] = useState(() => getRecentChecks());

  // Re-read on focus so cross-tab updates land here too.
  useEffect(() => {
    const refresh = () => setItems(getRecentChecks());
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  if (!items.length) return null;

  const handleRerun = (entry) => {
    const params = new URLSearchParams();
    params.set("med", entry.med_id);
    const productSlug = entry.product_id || entry.product_name;
    if (productSlug) params.set("product", productSlug);
    navigate(`/check?${params.toString()}`);
  };

  const handleClear = () => {
    clearRecentChecks();
    setItems([]);
  };

  return (
    <section
      data-testid="recent-checks-section"
      className="bg-cream-50 border-y border-surface-line py-10 md:py-12"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex items-end justify-between mb-5 sm:mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
              Pick up where you left off
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink mt-2 leading-[1.08]">
              Your recent checks
            </h2>
          </div>
          <button
            type="button"
            data-testid="recent-checks-clear"
            onClick={handleClear}
            className="inline-flex items-center gap-1.5 text-[12px] text-ink-mute hover:text-ink transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear
          </button>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it, idx) => {
            const style = VERDICT_STYLE[it.verdict] || VERDICT_STYLE.INSUFFICIENT_DATA;
            const Icon = style.Icon;
            return (
              <li
                key={`${it.med_id}-${it.product_id || it.product_name}-${idx}`}
                data-testid={`recent-check-${idx}`}
                className="rounded-2xl bg-white border border-surface-line p-4 flex flex-col gap-3 transition-all hover:shadow-card hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
                    style={{ background: style.bg, color: style.color }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-ink leading-snug truncate">
                      {it.med_name} × {it.product_name}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px]">
                      <span
                        className="font-bold uppercase tracking-[0.18em]"
                        style={{ color: style.color }}
                      >
                        {style.label}
                      </span>
                      {typeof it.safety_score === "number" && (
                        <span className="text-ink-mute tabular-nums">
                          · {it.safety_score}/100
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`recent-check-rerun-${idx}`}
                  onClick={() => handleRerun(it)}
                  className="inline-flex items-center justify-center gap-1.5 h-10 rounded-xl bg-ink text-cream-50 text-[13px] font-semibold transition-all hover:bg-ink-soft hover:scale-[1.01] active:scale-[0.99]"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Re-run
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
